import type { components } from "@/lib/api/generated/backend";
import { formatRateWithoutCurrency, NO_CURRENCY_HINT } from "@/lib/utils";

/**
 * Currency of a consultant's expected hourly rate (backend#883).
 *
 * Single source of truth for every surface that shows or edits the rate: the
 * signup, the admin consultant form, detail and list, and the admin
 * application detail. The value is nullable end-to-end: rates entered before
 * backend#883 carry no currency and were deliberately not backfilled, so
 * "absent", "null" and "unrecognised" all mean *unknown* and render digits
 * only, never a guessed unit.
 */
export type RateCurrency = NonNullable<
  components["schemas"]["ConsultantUpdate"]["hourly_rate_currency"]
>;

export const RATE_CURRENCIES: readonly RateCurrency[] = ["DKK", "SEK", "NOK", "EUR", "GBP", "USD"];

export const isRateCurrency = (value: unknown): value is RateCurrency =>
  RATE_CURRENCIES.some(currency => currency === value);

/** Narrow a value off the wire; anything unknown becomes `null`. */
export const toRateCurrency = (value: unknown): RateCurrency | null =>
  isRateCurrency(value) ? value : null;

/** Signup step 2 stores ISO country codes; the rate currency defaults from it. */
const COUNTRY_CURRENCY: Record<string, RateCurrency> = {
  DK: "DKK",
  SE: "SEK",
  NO: "NOK",
  GB: "GBP",
};

/**
 * The currency pre-selected next to the signup's hourly rate: DK→DKK, SE→SEK,
 * NO→NOK, GB→GBP, anything else (or no country) → EUR (backend#883).
 */
export const defaultRateCurrency = (country: string | null | undefined): RateCurrency =>
  COUNTRY_CURRENCY[(country ?? "").trim().toUpperCase()] ?? "EUR";

/**
 * Format a profile rate. With a known currency the ISO code is appended
 * ("1.050 SEK" — the code, not "kr.", which is ambiguous across the Nordics);
 * without one the digits stand alone and callers show {@link NO_CURRENCY_HINT}.
 */
export const formatProfileRate = (amount: number, currency: unknown): string => {
  const digits = formatRateWithoutCurrency(amount);
  return isRateCurrency(currency) ? `${digits} ${currency}` : digits;
};

/** Tooltip for a rate: nothing to explain when the currency is known. */
export const rateCurrencyHint = (currency: unknown): string | undefined =>
  isRateCurrency(currency) ? undefined : NO_CURRENCY_HINT;

/**
 * The signup's effective currency: the applicant's explicit choice, or the
 * default for the country entered on step 2. Keeping the choice separate from
 * the default means a changed country still moves an untouched selector.
 */
export const resolveRateCurrency = (
  chosen: RateCurrency | "" | null | undefined,
  country: string | null | undefined
): RateCurrency => (chosen ? chosen : defaultRateCurrency(country));
